import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  getArrayLength,
  getHasElementsInArray,
  getSortComparator,
  includesIgnoreCase,
} from 'utils';
import UsersTCPanel from './components/UsersTCPanel';
import TermsAndConditionsContext from 'router/subrouters/DataRoom/providers/TermsAndConditions/TermsAndConditions.context';
import {
  DataRoomTermsAndConditions,
  DataRoomTermsAndConditionsSort,
} from 'router/subrouters/DataRoom/models/DataRoomTermsAndConditions';
import ResponsiveDropdown from 'components/ResponsiveDropdown';
import { ResponsiveDropdownRef } from 'components/ResponsiveDropdown/ResponsiveDropdown.component';

import './UsersTCSelect.styles.scss';

type UsersTCSelectProps = {
  className?: string;
  selectedId: string | null;
  onSelect: (item: DataRoomTermsAndConditions) => void;
};

const UsersTCSelect: FC<UsersTCSelectProps> = (props) => {
  const { className, selectedId, onSelect } = props;

  const [isShowingDeleted, setIsShowingDeleted] = useState(false);
  const [sort, setSort] = useState<DataRoomTermsAndConditionsSort>('recent');
  const [search, setSearch] = useState('');

  const { activeItems, deletedItems, onDelete, onRestore } = useContext(
    TermsAndConditionsContext,
  );

  const [responsiveDropdown, responsiveDropdownRef] =
    useCallbackRef<ResponsiveDropdownRef>();

  const { t } = useTranslation();

  const sortItems = useCallback(
    (items: DataRoomTermsAndConditions[]) =>
      [...items].sort((a, b) => {
        if (sort !== 'recent')
          return getSortComparator(a.name, b.name, sort === 'z-a');

        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      }),
    [sort],
  );

  const filteredItems = useMemo(() => {
    const source = isShowingDeleted ? deletedItems : activeItems;

    const filtered = search
      ? source.filter(({ name }) => includesIgnoreCase(name, search))
      : source;

    return sortItems(filtered);
  }, [isShowingDeleted, activeItems, deletedItems, search, sortItems]);

  const handleSelect = useCallback(
    (item: DataRoomTermsAndConditions) => {
      onSelect(item);
      setSearch('');
      responsiveDropdown?.close();
    },
    [onSelect, responsiveDropdown],
  );

  const selectedName = useMemo(
    () => activeItems.find(({ _id }) => _id === selectedId)?.name ?? null,
    [activeItems, selectedId],
  );

  const classes = classNames(
    'users-tc-select',
    { 'users-tc-select--has-value': !!selectedName },
    className,
  );

  const trigger = useMemo(
    () => (
      <Button variant="link" className={classes}>
        <span>{selectedName || t('selectTemplate')}</span>
        <ChevrondownIcon
          className={classNames({
            open: responsiveDropdown?.isOpen,
          })}
        />
      </Button>
    ),
    [classes, responsiveDropdown?.isOpen, selectedName, t],
  );

  if (
    !getHasElementsInArray(activeItems) &&
    !getHasElementsInArray(deletedItems)
  )
    return null;

  return (
    <ResponsiveDropdown
      ref={responsiveDropdownRef}
      trigger={trigger}
      modalProps={{ title: t('selectTemplate') }}
      dropdownProps={{ className: 'user-tc-select-dropdown' }}
    >
      <UsersTCPanel
        items={filteredItems}
        deletedCount={getArrayLength(deletedItems)}
        sort={sort}
        search={search}
        isShowingDeleted={isShowingDeleted}
        selectedId={selectedId}
        onSortChange={setSort}
        onSearchChange={setSearch}
        onToggleDeleted={() => setIsShowingDeleted((prev) => !prev)}
        onSelect={handleSelect}
        onDelete={onDelete}
        onRestore={onRestore}
      />
    </ResponsiveDropdown>
  );
};

export default UsersTCSelect;
